import { FileText, Mail, AlertCircle, Loader2, Search } from 'lucide-react';
import { useState, useEffect } from 'react';

export default function UmoorSehat() {
  const [newsletters, setNewsletters] = useState([]);
  const [filteredNewsletters, setFilteredNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Your configuration
  const API_KEY = 'AIzaSyCm63frGEG3nyKw8YatgsOh31gCzdrGDEE';
  const FOLDER_ID = '1pm4OVgESn5A5dPwalG3cKRughVQW-n0q';

  useEffect(() => {
    fetchNewsletters();
  }, []);

  useEffect(() => {
    // Filter newsletters based on search query
    if (searchQuery.trim() === '') {
      setFilteredNewsletters(newsletters);
    } else {
      const filtered = newsletters.filter(newsletter =>
        newsletter.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredNewsletters(filtered);
    }
  }, [searchQuery, newsletters]);

  const fetchNewsletters = async () => {
    try {
      setLoading(true);
      setError(null);

      // Google Drive API endpoint to list files in a folder
      const url = `https://www.googleapis.com/drive/v3/files?q='${FOLDER_ID}'+in+parents&key=${API_KEY}&fields=files(id,name,mimeType,createdTime,webViewLink,thumbnailLink)&orderBy=createdTime desc`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch newsletters from Google Drive');
      }

      const data = await response.json();

      // Filter for PDF files only
      const pdfFiles = data.files.filter(file =>
        file.mimeType === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf')
      );

      setNewsletters(pdfFiles);
    } catch (err) {
      console.error('Error fetching newsletters:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Extract date info from filename if possible (optional enhancement)
  const parseFileName = (fileName) => {
    // Remove file extension
    const nameWithoutExt = fileName.replace(/\.(pdf|PDF)$/, '');
    return nameWithoutExt;
  };

  return (
    <div className="min-h-screen bg-white">


      {/* Hero Section */}
      <div className="relative py-24 text-white overflow-hidden" style={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d66 100%)' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 right-20 w-64 h-64 rounded-full border-2 border-white"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h1 className="font-playfair text-5xl md:text-6xl font-bold mb-4">Umoor Sehat</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* Divider */}
        <div className="my-12 h-1 bg-gradient-to-r from-transparent via-dawoodi-gold/30 to-transparent rounded-full"></div>

        {/* Newsletters Section */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-playfair text-4xl font-bold text-dawoodi-navy mb-2">Newsletters</h2>
            </div>
            <button
              onClick={fetchNewsletters}
              disabled={loading}
              className="flex items-center space-x-2 bg-dawoodi-gold text-dawoodi-navy font-semibold px-4 py-2 rounded-lg hover:bg-yellow-300 transition-colors disabled:opacity-50 self-start md:self-auto"
            >
              <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search newsletters..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-dawoodi-navy/10 rounded-xl font-poppins focus:outline-none focus:border-dawoodi-gold transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="font-poppins text-sm text-gray-600 mt-2">
                Found {filteredNewsletters.length} newsletter{filteredNewsletters.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-12 h-12 text-dawoodi-gold animate-spin mb-4" />
              <p className="font-poppins text-gray-600">Loading newsletters...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="font-playfair font-bold text-red-800 text-lg mb-2">Unable to Load Newsletters</h3>
              <p className="font-poppins text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchNewsletters}
                className="bg-red-500 text-white font-semibold px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Newsletters Grid */}
          {!loading && !error && filteredNewsletters.length > 0 && (
            <div className="grid md:grid-cols-2 gap-6">
              {filteredNewsletters.map((newsletter) => (
                <a
                  key={newsletter.id}
                  href={newsletter.webViewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white border-2 border-dawoodi-navy/10 rounded-xl p-8 hover:border-dawoodi-gold hover:shadow-lg transition-all"
                >
                  <div className="flex items-start space-x-4">
                    <div className="text-4xl group-hover:scale-110 transition-transform">📄</div>
                    <div className="flex-1">
                      <h3 className="font-playfair font-bold text-dawoodi-navy text-lg mb-2 group-hover:text-dawoodi-gold transition-colors">
                        {parseFileName(newsletter.name)}
                      </h3>
                      <p className="font-poppins text-gray-600 text-sm mb-4">
                        {new Date(newsletter.createdTime).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })} at {new Date(newsletter.createdTime).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </p>
                      <div className="inline-flex items-center space-x-2 text-dawoodi-gold font-semibold group-hover:translate-x-1 transition-transform">
                        <span>View Newsletter</span>
                        <span>→</span>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && newsletters.length > 0 && filteredNewsletters.length === 0 && (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-12 text-center">
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="font-playfair font-bold text-gray-700 text-xl mb-2">No Results Found</h3>
              <p className="font-poppins text-gray-600 mb-4">
                No newsletters match your search "{searchQuery}"
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="bg-dawoodi-gold text-dawoodi-navy font-semibold px-6 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
              >
                Clear Search
              </button>
            </div>
          )}

          {/* Empty State - No videos at all */}
          {!loading && !error && newsletters.length === 0 && (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="font-playfair font-bold text-gray-700 text-xl mb-2">No Newsletters Found</h3>
              <p className="font-poppins text-gray-600">
                Check back soon for new health and wellness newsletters.
              </p>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="my-12 h-1 bg-gradient-to-r from-transparent via-dawoodi-gold/30 to-transparent rounded-full"></div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-dawoodi-navy to-dawoodi-navy text-white rounded-2xl p-12 text-center">
          <a
            href="mailto:umoor.sehat@anjuman-e-burhani.org"
            className="inline-flex items-center space-x-2 bg-dawoodi-gold text-dawoodi-navy font-bold px-8 py-4 rounded-xl hover:bg-yellow-300 transition-colors"
          >
            <Mail className="w-5 h-5" />
            <span>umoor.sehat@anjuman-e-burhani.org</span>
          </a>
        </div>
      </div>
    </div>
  );
}